import { Box, Typography } from '@mui/material';
import { CSSProperties, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { getPrice } from 'shared/lib/getPrice/getPrice';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import {
    BestPriceSelectedTooltipContent,
    BetterPriceAvailableTooltipContent,
    PostSubmissionSavingsTooltipContent,
} from './OrderItemSavingsTooltips';

import type { ItemSavingsResult } from 'entities/Order/lib/calculateOrderSavings/calculateOrderSavings';

const savingsGreenTextSx = {
    fontSize: '1.2rem',
    color: '#2CB353',
    fontWeight: 500,
    marginTop: '2px',
    flexShrink: 0,
} as const;

const potentialBadgeSx = {
    fontSize: '1.2rem',
    lineHeight: '1.3rem',
    fontWeight: 400,
    flexShrink: 0,
    display: 'inline-block',
    padding: '2px 4px',
    borderRadius: '4px',
    backgroundColor: '#FFEEBA',
    color: '#D84315',
    marginTop: '1px',
} as const;

const tooltipContainerStyle = () =>
    ({
        display: 'flex',
        marginTop: '-1px',
    } as CSSProperties);

interface OrderItemSavingsProps {
    itemSavingsResult: ItemSavingsResult | null;
    isPreSubmission: boolean;
    canSeeOrderSavings: boolean;
    orderId: string | null;
    itemId: string;
}

/**
 * Renders the appropriate savings label and amount for an order item.
 * Shows "Saved"/"Savings" $X whenever price paid is below average (savings vs average > 0).
 * Shows "Potential savings" $X when not at best price and potential > 0 (pre-submission only).
 */
export const OrderItemSavings = memo(
    ({
        itemSavingsResult,
        isPreSubmission,
        canSeeOrderSavings,
        orderId,
        itemId,
    }: OrderItemSavingsProps) => {
        const { t } = useTranslation();

        if (!canSeeOrderSavings || !itemSavingsResult || !orderId || !itemId)
            return null;

        const showSavedPost = !isPreSubmission && itemSavingsResult.savings > 0;

        const showSavingsPre = isPreSubmission && itemSavingsResult.savings > 0;

        const showPotentialPre =
            isPreSubmission &&
            !itemSavingsResult.isBestPriceSelected &&
            itemSavingsResult.potentialSavings > 0;

        const tooltipContentPre = itemSavingsResult.isBestPriceSelected ? (
            <BestPriceSelectedTooltipContent
                itemSavingsResult={itemSavingsResult}
            />
        ) : (
            <BetterPriceAvailableTooltipContent
                itemSavingsResult={itemSavingsResult}
                orderId={orderId}
                itemId={itemId}
            />
        );

        if (showSavedPost) {
            return (
                <DynamicTooltip
                    content={
                        <PostSubmissionSavingsTooltipContent
                            itemSavingsResult={itemSavingsResult}
                        />
                    }
                    maxWidth={320}
                    containerStyle={tooltipContainerStyle()}
                    interactive={true}
                >
                    <Box component="span" sx={{ display: 'flex' }}>
                        <Typography
                            data-testclass="order_item_savings_price"
                            component="span"
                            variant="caption"
                            sx={savingsGreenTextSx}
                        >
                            {getPrice(itemSavingsResult.savings)}
                        </Typography>
                    </Box>
                </DynamicTooltip>
            );
        }

        // Pre-submission: prefer potential savings at item level; only show savings when no potential
        if (showPotentialPre) {
            return (
                <DynamicTooltip
                    content={
                        <BetterPriceAvailableTooltipContent
                            itemSavingsResult={itemSavingsResult}
                            orderId={orderId}
                            itemId={itemId}
                        />
                    }
                    maxWidth={320}
                    containerStyle={tooltipContainerStyle()}
                    interactive={true}
                >
                    <Box component="span" sx={{ display: 'flex' }}>
                        <Typography
                            data-testclass="order_item_savings_price"
                            component="span"
                            variant="caption"
                            sx={potentialBadgeSx}
                        >
                            {t('Save amount')}{' '}
                            <span style={{ fontWeight: 600 }}>
                                {getPrice(itemSavingsResult.potentialSavings)}
                            </span>
                        </Typography>
                    </Box>
                </DynamicTooltip>
            );
        }

        if (showSavingsPre) {
            return (
                <DynamicTooltip
                    content={tooltipContentPre}
                    maxWidth={320}
                    containerStyle={tooltipContainerStyle()}
                    interactive={true}
                >
                    <Box component="span" sx={{ display: 'flex' }}>
                        <Typography
                            data-testclass="order_item_savings_price"
                            component="span"
                            variant="caption"
                            sx={savingsGreenTextSx}
                        >
                            {getPrice(itemSavingsResult.savings)}
                        </Typography>
                    </Box>
                </DynamicTooltip>
            );
        }

        return null;
    },
);

OrderItemSavings.displayName = 'OrderItemSavings';
